import React, {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  RefObject,
} from 'react';
import clsx from 'clsx';
import CloseIcon from '@mui/icons-material/Close';
import Editable, {
  EditableInstance,
} from 'core/components/Editable';
import ToolTip from 'core/components/Tooltip';
import DatePicker from 'core/components/DateTimePicker/SingleDatePicker';
import CalendarIcon from 'core/icons/calendar';
import {
  useUpdateToDoHandler,
} from 'pages/Dashboard/pages/Todos/hooks';
import ToDoDueDateValue from 'pages/Dashboard/pages/Todos/components/ToDoDueDateValue';
import {
  ToDo,
} from 'types/toDo';
import dayjs, {
  Dayjs,
} from 'utils/dayjs';
import {
  formatDateOnly,
} from 'utils/date';

type Parts = 'value' | 'emptyValue' | 'popover';

function stopEventPropagation(event: {
  stopPropagation: () => void;
}) {
  event.stopPropagation();
}

function stopInteractiveEvent(event: ReactMouseEvent<HTMLButtonElement>) {
  event.preventDefault();
  event.stopPropagation();
}

type Props = {
  toDo?: ToDo;
  className?: string;
  classes?: Partial<Record<Parts, string>>;
  displayVariant?: 'pill' | 'text';
  hideEditIcon?: boolean;
};

type IdleViewProps = {
  dueDate?: string | Date | null;
  classes?: Partial<Record<Exclude<Parts, 'popover'>, string>>;
  displayVariant?: 'pill' | 'text';
  emptyDueDateLabel: string;
  onClear?: () => void | Promise<void>;
  disabled?: boolean;
};

function EditableDueDateIdleView({
  dueDate,
  classes,
  displayVariant = 'pill',
  emptyDueDateLabel,
  onClear,
  disabled = false,
}: IdleViewProps) {
  const isTextVariant = displayVariant === 'text';
  const isClearable = Boolean(dueDate && onClear && !disabled);

  return (
    <span className="group relative inline-flex max-w-full items-center">
      {dueDate ? (
        <ToDoDueDateValue
          dueDate={dueDate}
          className={clsx(isClearable && 'pr-7', classes?.value)}
          emptyClassName={classes?.emptyValue}
          variant={displayVariant}
        />
      ) : (
        <span
          className={clsx(
            'inline-flex max-w-full items-center gap-1.5 whitespace-nowrap !text-xs font-medium leading-5',
            {
              'text-slate-500': isTextVariant,
              'rounded-full border border-dashed border-gray-300 bg-gray-50 px-2.5 py-1 text-gray-500 transition-colors hover:border-gray-400 hover:bg-gray-100': !isTextVariant,
            },
            classes?.emptyValue,
          )}
        >
          <CalendarIcon className="!h-4 !w-4 shrink-0 !text-gray-400" />
          <span className="truncate">{emptyDueDateLabel}</span>
        </span>
      )}
      {isClearable && (
        <ToolTip title="Remove due date">
          <button
            type="button"
            className={clsx(
              'absolute right-0 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-full border border-transparent bg-white text-gray-400 opacity-0 shadow-sm transition-all hover:bg-gray-100 hover:text-gray-700 group-hover:opacity-100 group-focus-within:opacity-100',
              'focus-visible:border-gray-300 focus-visible:opacity-100',
            )}
            aria-label="Clear due date"
            onMouseDown={stopInteractiveEvent}
            onClick={(event) => {
              stopInteractiveEvent(event);
              onClear?.();
            }}
          >
            <CloseIcon className="!h-3 !w-3" />
          </button>
        </ToolTip>
      )}
    </span>
  );
}

export default function EditableDueDate({
  toDo,
  className,
  classes,
  displayVariant = 'pill',
  hideEditIcon = false,
}: Props) {
  const editableRef = useRef<EditableInstance>(null as unknown as EditableInstance);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const ignoreInitialEmptyUpdateRef = useRef(false);
  const suppressNextRowClickRef = useRef(false);
  const [isEditing, setIsEditing] = useState(false);
  const [value, setValue] = useState<Dayjs | null>(toDo?.dueDate ? dayjs(toDo.dueDate) : null);
  const {
    asyncHandler: updateToDo,
    isLoading: isUpdating,
  } = useUpdateToDoHandler({ successMessage: 'To-do was updated successfully!' });

  useEffect(() => {
    setValue(toDo?.dueDate ? dayjs(toDo.dueDate) : null);
  }, [toDo?.dueDate]);

  const currentValue = useMemo(
    () => (toDo?.dueDate ? formatDateOnly(dayjs(toDo.dueDate)) : null),
    [toDo?.dueDate],
  );
  const emptyDueDateLabel = 'No due date';

  const resetValue = useCallback(() => {
    ignoreInitialEmptyUpdateRef.current = false;
    setValue(toDo?.dueDate ? dayjs(toDo.dueDate) : null);
  }, [toDo?.dueDate]);

  const closeEditor = useCallback(() => {
    ignoreInitialEmptyUpdateRef.current = false;
    setIsEditing(false);
    editableRef.current?.setIsEdit(false);
  }, []);

  const handleUpdate = useCallback(async (nextValue: Dayjs | null) => {
    const formattedNextValue = nextValue ? formatDateOnly(dayjs(nextValue)) : null;
    const shouldIgnoreInitialEmptyUpdate = (
      formattedNextValue === null
      && currentValue === null
      && ignoreInitialEmptyUpdateRef.current
    );

    setValue(nextValue);

    ignoreInitialEmptyUpdateRef.current = false;

    if (!shouldIgnoreInitialEmptyUpdate) {
      if (formattedNextValue === currentValue) {
        closeEditor();
      } else {
        await updateToDo({
          toDoId: toDo?.toDoId ?? 0,
          dueDate: formattedNextValue,
        });
        closeEditor();
      }
    }
  }, [closeEditor, currentValue, toDo?.toDoId, updateToDo]);

  const handleClear = useCallback(async () => {
    await handleUpdate(null);
  }, [handleUpdate]);

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      const { target } = event;
      const isInsideEditor = target instanceof Node && rootRef.current?.contains(target);
      const isInsideDatePopover = target instanceof Element
        && Boolean(target.closest('.populate-date-picker-popover'));

      if (isInsideEditor || isInsideDatePopover) {
        return;
      }

      const clickedTableRow = target instanceof Element
        ? target.closest('.populate-table-row')
        : null;

      if (clickedTableRow) {
        suppressNextRowClickRef.current = true;
        event.preventDefault();
        event.stopPropagation();
      }

      inputRef.current?.blur();
      window.setTimeout(() => {
        editableRef.current?.triggerSubmit();
      }, 0);
    };

    const handleClickCapture = (event: MouseEvent) => {
      if (!suppressNextRowClickRef.current) {
        return;
      }

      suppressNextRowClickRef.current = false;
      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener('mousedown', handlePointerDown, true);
    document.addEventListener('click', handleClickCapture, true);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true);
      document.removeEventListener('click', handleClickCapture, true);
    };
  }, [isEditing]);

  return (
    <div ref={rootRef} className="min-w-0 overflow-visible">
      <Editable
        instanceRef={editableRef}
        readonly={toDo?.isAbandoned || isUpdating}
        idleView={(
          <EditableDueDateIdleView
            dueDate={toDo?.dueDate}
            classes={{
              value: classes?.value,
              emptyValue: classes?.emptyValue,
            }}
            displayVariant={displayVariant}
            emptyDueDateLabel={emptyDueDateLabel}
            onClear={handleClear}
            disabled={toDo?.isAbandoned || isUpdating}
          />
        )}
        classes={{
          idleRoot: clsx('!min-w-0 !justify-start !items-center !px-0 !py-0 !border-0', className),
          editableRoot: clsx('!w-full !justify-start !overflow-visible', className),
          iconRoot: hideEditIcon ? '!hidden' : undefined,
        }}
        showActions={false}
        isLoading={isUpdating}
        controlRef={inputRef as RefObject<HTMLElement>}
        tooltip="Update Due Date"
        onSubmit={() => {
          setIsEditing(false);
          return true;
        }}
        onCancel={() => {
          setIsEditing(false);
          resetValue();
        }}
        onShowControl={(event) => {
          event.preventDefault();
          event.stopPropagation();
          ignoreInitialEmptyUpdateRef.current = currentValue === null;
          setIsEditing(true);
        }}
      >
        {(onSubmit, onCancel) => (
          <div
            role="button"
            tabIndex={-1}
            className={clsx('flex items-center gap-2 overflow-visible', classes?.popover ?? 'w-52')}
            onMouseDown={stopEventPropagation}
            onClick={stopEventPropagation}
            onKeyDown={stopEventPropagation}
          >
            <DatePicker
              inputRef={inputRef as RefObject<HTMLInputElement>}
              value={value}
              onChange={handleUpdate}
              className="!rounded"
              containerClassName="!w-full"
              inputContainerClassName="!w-full"
              showDateButtons={false}
              disabled={isUpdating}
              slotProps={{
                desktopPaper: {
                  onMouseDown: stopEventPropagation,
                  onClick: stopEventPropagation,
                },
              }}
              inputProps={{
                onClick: (event) => {
                  event.stopPropagation();
                },
                onMouseDown: (event) => {
                  event.stopPropagation();
                },
                onKeyDown: (event) => {
                  event.stopPropagation();

                  if (event.key === 'Escape') {
                    onCancel();
                    return;
                  }

                  if (event.key === 'Enter') {
                    (event.currentTarget as HTMLInputElement).blur();
                    window.setTimeout(() => {
                      onSubmit();
                    }, 0);
                  }
                },
              }}
            />
          </div>
        )}
      </Editable>
    </div>
  );
}
